import React from "react";
import { useOutletContext } from "react-router-dom";
import Avatar from "../components/Avatar";
import CrIcon from "../components/CrIcon";
import { Auth } from "../helpers/auth";

/**
 * Account: the page where a person changes THEIR OWN sign-in details.
 *
 * Every member gets it, not just workspace admins: it is about one account,
 * never about the workspace. Nothing here changes a setting a teammate would
 * notice (that is Control Room).
 *
 * The email is shown but never editable. A BossDrop workspace is filed under
 * the owner's address in two dozen tables, and every connected tool (Google,
 * Microsoft, Slack, Teams, the inbound email routing) is keyed on it, so
 * changing it is a migration we run by hand, not a settings field. The card
 * says so in plain words and points at support.
 */

const SUPPORT_EMAIL = "support@bossdrop.ai";
// A name or address that would otherwise push the profile row wider than a
// phone: one line, cut with an ellipsis.
const ONE_LINE = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const MIN_PASSWORD_LENGTH = 12;
const NAME_MAX_LENGTH = 80;

// Mirrors the server's PATCH /auth/profile rule so the box cannot send
// something the server will only bounce: control characters out, runs of
// whitespace collapsed, trimmed, capped. Empty is allowed and clears the name.
export function normalizeAccountName(value) {
  return String(value == null ? "" : value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, NAME_MAX_LENGTH);
}

// The six levels normalizeWorkspaceSecurityLevel can return. All six, because
// falling back to "Member" told a viewer and a custom-permission account they
// had an access level they do not have, on the page whose job is to state it.
const LEVEL_LABELS = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  member: "Member",
  viewer: "Viewer",
  custom: "Custom",
};

// Only owner and admin hold canManageWorkspace, so a manager holds exactly the
// member permission set. The hint says that rather than inventing a team the
// backend has no idea about. Owner and admin are no longer the same thing:
// changing the Team directory and deleting the workspace are the owner's alone.
const LEVEL_HINTS = {
  owner:
    "You can change anything in this workspace, including who belongs to it.",
  admin:
    "You can change this workspace's settings. Only the owner can change the Team directory or delete the workspace.",
  manager: "You can review the workspace. Admins change its settings.",
  member: "You can review the workspace. Admins change its settings.",
  viewer: "You can read the workspace but not change it.",
  custom: "Your access was set individually. Ask an admin what it covers.",
};

function levelLabel(value) {
  const key = String(value || "member").toLowerCase();
  if (LEVEL_LABELS[key]) return LEVEL_LABELS[key];
  // A level added to the server after this build shipped: show what the server
  // called it. Guessing is what printed "Member" for a viewer.
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : LEVEL_LABELS.member;
}

function levelHint(value, permissions) {
  const key = String(value || "member").toLowerCase();
  if (LEVEL_HINTS[key]) return LEVEL_HINTS[key];
  // Unknown level: say only what /auth/me actually reports it can do.
  return permissions?.canManageWorkspace
    ? "You can change this workspace's settings."
    : "You can review the workspace. Admins change its settings.";
}

function formatJoinDate(value) {
  if (!value) return "";
  const stamp = new Date(value);
  if (Number.isNaN(stamp.getTime())) return "";
  try {
    return stamp.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return stamp.toISOString().slice(0, 10);
  }
}

/* ---------------------------------------------------------------------------
   Every server error code this page can see, in words that say what to do
   next. Anything unrecognised falls back to the card's own sentence, so a new
   code on the server can never print a bare token like "weak_password".
   -------------------------------------------------------------------------- */

function profileErrorMessage(code) {
  switch (code) {
    case "invalid_name":
      return "That name has characters BossDrop cannot store. Try letters, numbers, spaces and ordinary punctuation.";
    case "name_too_long":
      return `Names can be up to ${NAME_MAX_LENGTH} characters. Shorten it and save again.`;
    case "rate_limited":
      return "That is a lot of changes in a row. Wait a minute, then save again.";
    case "unauthorized":
      return "Your session has expired. Sign in again, then save your name.";
    default:
      return "The name did not save. Try again in a moment.";
  }
}

function passwordErrorMessage(code) {
  switch (code) {
    case "wrong_password":
      return "That is not your current password. Type it again, or use the link below to set a new one by email.";
    case "weak_password":
      return `Pick a password with at least ${MIN_PASSWORD_LENGTH} characters.`;
    case "same_password":
      return "That is the password you already have. Pick a different one.";
    case "no_password_set":
      return "This account has never had a password: you sign in with Google, or your invite link was never used. Send yourself a link below to set one.";
    case "rate_limited":
      return "Too many attempts in a row. Wait a few minutes, then try again.";
    case "unauthorized":
      return "Your session has expired. Sign in again, then change your password.";
    default:
      return "The password did not change. Try again in a moment.";
  }
}

// Takes the method, because "get a fresh code" is the wrong instruction for
// both factors: an authenticator code is only good for 30 seconds and cannot
// be re-sent, and a texted code is good for ten minutes, so telling an SMS
// user to throw it away threw away nine good minutes.
function mfaDisableErrorMessage(code, method) {
  switch (code) {
    case "wrong_password":
      return "That is not your password. Type it again.";
    case "invalid_code":
      return method === "sms"
        ? "That code did not match. Check the text again, or press Text me a code for a new one."
        : "That code did not match. Your app shows a new one every 30 seconds, so type the one showing now.";
    case "mfa_not_enabled":
      return "Two-factor is already off for this account.";
    case "no_password_set":
      return "This account has never had a password, so BossDrop cannot check one. Use Forgot password on the sign-in screen to set one, then come back.";
    case "rate_limited":
      return "Too many attempts in a row. Wait a few minutes, then try again.";
    case "unauthorized":
      return "Your session has expired. Sign in again, then try once more.";
    default:
      return "Two-factor did not turn off. Try again in a moment.";
  }
}

// POST /auth/mfa/send-code, the signed-in branch: the only way to get the
// texted code that turning SMS two-factor off asks for.
function sendCodeErrorMessage(code) {
  switch (code) {
    case "mfa_code_recently_sent":
      return "A code has just gone out. Give it a minute before asking for another.";
    case "sms_not_pending":
      return "This account is not set up for texted codes, so there is nothing to send.";
    case "phone_missing":
      return `No phone number is stored for this account. Email ${SUPPORT_EMAIL} and we will sort it out.`;
    case "rate_limited":
      return "Too many attempts in a row. Wait a few minutes, then try again.";
    case "unauthorized":
      return "Your session has expired. Sign in again, then try once more.";
    default:
      return "The text did not send. Try again in a moment.";
  }
}

function sessionsErrorMessage(code) {
  switch (code) {
    case "rate_limited":
      return "Too many attempts in a row. Wait a few minutes, then try again.";
    case "unauthorized":
      return "Your session has expired. Sign in again, then try once more.";
    default:
      return "The other devices were not signed out. Try again in a moment.";
  }
}

function errorCodeOf(err) {
  return String(err?.data?.error || "");
}

/* ---------------------------------------------------------------------------
   Small pieces
   -------------------------------------------------------------------------- */

// One read-only fact. A depth-3 tile, the same surface the Control Room uses
// for the things you look at rather than edit.
function FactTile({ label, value, hint }) {
  return (
    <div className="crTile">
      <div className="muted small" style={{ fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontWeight: 800, marginTop: 2, overflowWrap: "anywhere" }}>
        {value}
      </div>
      {hint ? (
        <div className="muted small" style={{ marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

// A row of FactTile. The shared .crFieldRow aligns its cells to `start`, which
// is right for a form control sitting above its own hint and wrong for a row of
// stats: one tile whose hint wraps to two lines leaves the others hanging short
// (an ordinary member's Access level hint does exactly that). Stats stretch to
// the tallest instead. Scoped here, so the Name and Password rows keep `start`.
function FactRow({ children }) {
  return (
    <div className="crFieldRow" style={{ alignItems: "stretch" }}>
      {children}
    </div>
  );
}

function ResultLine({ tone, badge, children }) {
  return (
    <div
      className="muted small"
      role={tone === "bad" ? "alert" : "status"}
      style={{ marginTop: 10 }}
    >
      <span className={`chip ${tone}`}>{badge}</span> {children}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The page
   -------------------------------------------------------------------------- */

export default function Account() {
  const ctx = useOutletContext() || {};
  // First paint comes from the app's copy of the signed-in user, which this
  // page keeps current through updateCurrentUser; the page's own /auth/me then
  // refreshes it in the background. Before, a saved name only reached the
  // app on a full reload, so leaving the page and coming back showed the old
  // name in the box until F5.
  const [me, setMe] = React.useState(ctx.currentUser || null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const updateCurrentUserRef = React.useRef(ctx.updateCurrentUser);
  updateCurrentUserRef.current = ctx.updateCurrentUser;

  const [name, setName] = React.useState(ctx.currentUser?.name || "");
  const [nameTouched, setNameTouched] = React.useState(false);
  // Read inside the load below, which must never replace a name being typed.
  const nameTouchedRef = React.useRef(false);
  const [savingName, setSavingName] = React.useState(false);
  const [nameSaved, setNameSaved] = React.useState(false);
  const [nameError, setNameError] = React.useState("");
  // The name the server last confirmed, straight from PATCH /auth/profile.
  // Reading the confirmation off `me` instead meant that when this page's own
  // GET /auth/me had failed (`me` null), a save that really stored a name
  // reported, under a green Saved chip, that the name was now blank.
  const [confirmedName, setConfirmedName] = React.useState(null);

  const [pwCurrent, setPwCurrent] = React.useState("");
  const [pwNext, setPwNext] = React.useState("");
  const [pwConfirm, setPwConfirm] = React.useState("");
  const [pwBusy, setPwBusy] = React.useState(false);
  const [pwError, setPwError] = React.useState("");
  const [pwDone, setPwDone] = React.useState(false);
  // "" when there is nothing to offer, "set" for an account that has never had
  // a password, "reset" for one whose owner just mistyped it. The button's
  // wording follows, because "set a password" is nonsense said to someone who
  // plainly has one.
  const [pwNeedsLink, setPwNeedsLink] = React.useState("");
  const [linkBusy, setLinkBusy] = React.useState(false);
  const [linkSent, setLinkSent] = React.useState(false);

  const [offOpen, setOffOpen] = React.useState(false);
  const [offPassword, setOffPassword] = React.useState("");
  const [offCode, setOffCode] = React.useState("");
  const [offBusy, setOffBusy] = React.useState(false);
  const [offError, setOffError] = React.useState("");
  const [offNote, setOffNote] = React.useState("");
  const [mfaTurnedOff, setMfaTurnedOff] = React.useState(false);
  const [codeBusy, setCodeBusy] = React.useState(false);
  const [codeSent, setCodeSent] = React.useState("");
  const [codeError, setCodeError] = React.useState("");

  const [revokeOpen, setRevokeOpen] = React.useState(false);
  const [revokeBusy, setRevokeBusy] = React.useState(false);
  const [revokeDone, setRevokeDone] = React.useState(false);
  const [revokeError, setRevokeError] = React.useState("");

  const refresh = React.useCallback(async () => {
    try {
      const data = await Auth.me();
      const user = data?.user || null;
      setMe(user);
      setLoadFailed(!user);
      if (user) updateCurrentUserRef.current?.(user);
      return user;
    } catch {
      setLoadFailed(true);
      return null;
    }
  }, []);

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const data = await Auth.me();
        if (dead) return;
        const user = data?.user || null;
        setMe(user);
        setLoadFailed(!user);
        if (!user) return;
        updateCurrentUserRef.current?.(user);
        // The server's name wins over the first paint, which can be older,
        // but never over a name being typed.
        if (!nameTouchedRef.current) setName(user.name || "");
      } catch {
        if (!dead) setLoadFailed(true);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  const email = me?.email || ctx.currentUserEmail || "";
  const workspaceEmail = me?.workspaceOwnerEmail || ctx.workspaceOwnerEmail || "";
  // NO "member" fallback. The server now answers `securityLevel: null` for an
  // account that is in no workspace, and printing "Member" for it told someone
  // they had an access level they do not have -- on the page whose whole job is
  // to state it. An empty level plus no workspace is reported as exactly that.
  const securityLevel = me?.securityLevel || ctx.workspaceSecurityLevel || "";
  const permissions = me?.permissions || ctx.workspacePermissions || null;
  const joined = formatJoinDate(me?.createdAt);
  const mfaOn = !!me?.mfaEnabled;
  const mfaMethod = String(me?.mfaMethod || "");
  const maskedPhone = me?.maskedPhone || "";
  const ownWorkspace =
    !!workspaceEmail &&
    !!email &&
    workspaceEmail.toLowerCase() === email.toLowerCase();

  const savedName = confirmedName != null ? confirmedName : me?.name || "";
  const cleanedName = normalizeAccountName(name);
  const nameDirty = nameTouched && cleanedName !== savedName;

  /* ---- Profile ---- */

  async function saveName(e) {
    e.preventDefault();
    if (savingName) return;
    setSavingName(true);
    setNameError("");
    setNameSaved(false);
    try {
      const result = await Auth.updateProfile({ name: cleanedName });
      const next = typeof result?.name === "string" ? result.name : cleanedName;
      setName(next);
      setNameTouched(false);
      nameTouchedRef.current = false;
      setNameSaved(true);
      setConfirmedName(next);
      setMe((current) => (current ? { ...current, name: next } : current));
      updateCurrentUserRef.current?.({ name: next });
    } catch (err) {
      setNameError(profileErrorMessage(errorCodeOf(err)));
    } finally {
      setSavingName(false);
    }
  }

  /* ---- Password ---- */

  async function changePassword(e) {
    e.preventDefault();
    if (pwBusy) return;
    setPwDone(false);
    setLinkSent(false);

    if (!pwCurrent) {
      setPwNeedsLink("");
      setPwError("Type your current password first.");
      return;
    }
    if (pwNext.length < MIN_PASSWORD_LENGTH) {
      setPwNeedsLink("");
      setPwError(
        `Pick a new password with at least ${MIN_PASSWORD_LENGTH} characters.`,
      );
      return;
    }
    if (pwNext !== pwConfirm) {
      setPwNeedsLink("");
      setPwError(
        "The new password and the confirmation are not the same. Type them both again.",
      );
      return;
    }
    if (pwNext === pwCurrent) {
      setPwNeedsLink("");
      setPwError("That is the password you already have. Pick a different one.");
      return;
    }

    setPwBusy(true);
    setPwError("");
    setPwNeedsLink("");
    try {
      await Auth.changePassword({
        currentPassword: pwCurrent,
        newPassword: pwNext,
      });
      setPwCurrent("");
      setPwNext("");
      setPwConfirm("");
      setPwDone(true);
    } catch (err) {
      const code = errorCodeOf(err);
      setPwNeedsLink(
        code === "no_password_set"
          ? "set"
          : code === "wrong_password"
            ? "reset"
            : "",
      );
      setPwError(passwordErrorMessage(code));
    } finally {
      setPwBusy(false);
    }
  }

  async function emailPasswordLink() {
    if (linkBusy) return;
    setLinkBusy(true);
    setLinkSent(false);
    try {
      await Auth.requestReset({
        email,
        origin: window.location.origin,
        returnTo: "/account",
      });
      setLinkSent(true);
    } catch {
      // The server answers the same way whether or not the address exists, so
      // a failure here is the network, not the account.
      setPwError(
        "The link did not send. Check your connection and try again in a moment.",
      );
    } finally {
      setLinkBusy(false);
    }
  }

  /* ---- Two-factor ---- */

  // Puts the turn-off panel away and empties it. Called from every route out of
  // the panel, so a password typed into it is never left sitting in state.
  function closeOffPanel() {
    setOffOpen(false);
    setOffPassword("");
    setOffCode("");
    setOffError("");
    setCodeSent("");
    setCodeError("");
  }

  // Text-message two-factor only. Turning it off asks for a current code, and
  // once a person is fully signed in there is no code stored any more: the
  // sign-in that let them in cleared it. So the panel has to ask for a new one,
  // and it asks on a press rather than on open, because this sends a real text.
  async function sendDisableCode() {
    if (codeBusy) return;
    setCodeBusy(true);
    setCodeSent("");
    setCodeError("");
    try {
      const result = await Auth.resendMfaCode();
      const to = result?.maskedPhone || maskedPhone;
      setCodeSent(
        to
          ? `A code is on its way to ${to}. It is good for 10 minutes.`
          : "A code is on its way. It is good for 10 minutes.",
      );
    } catch (err) {
      setCodeError(sendCodeErrorMessage(errorCodeOf(err)));
    } finally {
      setCodeBusy(false);
    }
  }

  async function disableMfa(e) {
    e.preventDefault();
    if (offBusy) return;
    if (!offPassword) {
      setOffError("Type your password to confirm this is you.");
      return;
    }
    if (offCode.length < 6) {
      setOffError(
        mfaMethod === "sms"
          ? "Enter the 6-digit code from the text."
          : "Enter the 6-digit code from your authenticator app.",
      );
      return;
    }
    setOffBusy(true);
    setOffError("");
    setOffNote("");
    try {
      await Auth.disableMfa({ password: offPassword, code: offCode });
      closeOffPanel();
      setMfaTurnedOff(true);
      await refresh();
    } catch (err) {
      const code = errorCodeOf(err);
      // Two-factor was turned off somewhere else between loading this page and
      // pressing the button. Re-read the account rather than printing "Still
      // on" over a card that has just been told the opposite.
      if (code === "mfa_not_enabled") {
        closeOffPanel();
        setOffNote(
          "Two-factor was already off, so there was nothing to turn off.",
        );
        await refresh();
        return;
      }
      // The session died under us: the same staleness, one level up. The
      // page's own banner is the honest thing to show.
      if (code === "unauthorized") {
        setOffError(mfaDisableErrorMessage(code, mfaMethod));
        await refresh();
        return;
      }
      setOffError(mfaDisableErrorMessage(code, mfaMethod));
    } finally {
      setOffBusy(false);
    }
  }

  /* ---- Sessions ---- */

  async function revokeOthers() {
    if (revokeBusy) return;
    setRevokeBusy(true);
    setRevokeError("");
    try {
      await Auth.revokeOtherSessions();
      setRevokeOpen(false);
      setRevokeDone(true);
    } catch (err) {
      setRevokeError(sessionsErrorMessage(errorCodeOf(err)));
    } finally {
      setRevokeBusy(false);
    }
  }

  /* ---- Render ---- */

  const mfaStateLabel = !mfaOn
    ? "Off"
    : mfaMethod === "sms"
      ? "On, text message"
      : "On, authenticator app";
  const mfaStateTone = mfaOn ? "ok" : "warn";
  const mfaStateCopy = !mfaOn
    ? "Right now your password is the only thing between someone else and this account."
    : mfaMethod === "sms"
      ? `Signing in asks for a code texted to ${maskedPhone || "your phone"}.`
      : "Signing in asks for the 6-digit code in your authenticator app.";

  return (
    <section className="panel controlRoom" style={{ overflowY: "hidden" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>Account</h2>
        <span className={`chip ${mfaStateTone}`}>
          Two-factor: {mfaOn ? "on" : "off"}
        </span>
      </div>
      <p className="muted" style={{ marginTop: 6 }}>
        Your own sign-in details. Nothing on this page changes the workspace or
        anything your teammates see.
      </p>

      {loadFailed ? (
        <div className="infoBanner" style={{ marginTop: 12 }} role="alert">
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Could not load your account
          </div>
          <div className="muted small">
            Reload the page. If it keeps happening, sign out and sign in again.
          </div>
        </div>
      ) : null}

      {/* ---------------- Profile ---------------- */}
      <div className="crGroup">
        <CrIcon name="person" />
        Profile
      </div>

      <div className="panelSub">
        <h3 className="crTitle">
          <CrIcon name="person" size={18} />
          Your name
        </h3>
        <p className="muted">
          BossDrop signs its messages with this, and your teammates see it on
          anything you approve.
        </p>

        {/* Live preview: the badge follows the box as you type, the same
            initials-and-color badge the Team directory shows for you. */}
        <div className="bdAvatarRow" style={{ marginTop: 12 }}>
          <Avatar name={cleanedName} email={email} size={44} />
          <div className="bdAvatarRow__text">
            <strong style={ONE_LINE}>{cleanedName || email || "You"}</strong>
            {email && cleanedName ? (
              <span className="muted small" style={ONE_LINE}>
                {email}
              </span>
            ) : null}
          </div>
        </div>

        <form onSubmit={saveName}>
          <div className="crFieldRow" style={{ marginTop: 12 }}>
            <label className="crField">
              <span className="crFieldLabel">Name</span>
              <input
                className="inputFancy"
                type="text"
                autoComplete="name"
                maxLength={NAME_MAX_LENGTH}
                placeholder="Your name"
                value={name}
                disabled={savingName}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameTouched(true);
                  nameTouchedRef.current = true;
                  setNameSaved(false);
                  if (nameError) setNameError("");
                }}
              />
              <span className="muted small">
                Up to {NAME_MAX_LENGTH} characters. Leave it empty and BossDrop
                goes by your email address instead.
              </span>
            </label>
          </div>

          <div className="ctaRow">
            <button
              type="submit"
              className="btn primary"
              disabled={savingName || !nameDirty}
            >
              {savingName ? "Saving..." : "Save name"}
            </button>
          </div>
        </form>

        {nameSaved ? (
          <ResultLine tone="ok" badge="Saved">
            Your name is now{" "}
            <strong>{savedName || "blank, so BossDrop uses your email"}</strong>.
          </ResultLine>
        ) : null}
        {nameError ? (
          <ResultLine tone="bad" badge="Not saved">
            {nameError}
          </ResultLine>
        ) : null}

        <div className="crSubGroup">
          {/* A fact, not a field. Rendered as an input it was pixel for pixel
              the Name box above it, took a focus ring in the tab order and
              showed a caret, so it read as something you could edit. */}
          <FactRow>
            <FactTile label="Email" value={email || "Not set"} />
          </FactRow>
          {/* Full width, outside the field: in a 280px grid cell this
              explanation wrapped to five cramped lines. */}
          <p className="muted small" style={{ margin: "10px 0 0" }}>
            This address is the key your workspace is filed under, and every tool
            you have connected (calendar, Slack, Teams, the BossDrop inbox) is
            filed under it too, so it is not something to swap in a settings box.
            Email{" "}
            <a
              className="authLink"
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                "Change my BossDrop sign-in email",
              )}`}
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            and we move the whole account across for you.
          </p>
        </div>

        <div className="crSubGroup">
          <FactRow>
            <FactTile
              label="Access level"
              value={workspaceEmail ? levelLabel(securityLevel) : "No workspace"}
              hint={
                workspaceEmail
                  ? levelHint(securityLevel, permissions)
                  : "This account is not part of a workspace, so the rest of the hub has nothing to show. Ask whoever runs your workspace to add your address to their Team directory."
              }
            />
            <FactTile
              label="Workspace"
              value={workspaceEmail || "None"}
              hint={
                !workspaceEmail
                  ? "You have not been added to one yet."
                  : ownWorkspace
                    ? "This is your own workspace."
                    : "The workspace you were added to."
              }
            />
            <FactTile
              label="Member since"
              value={joined || "Not recorded"}
              hint={joined ? "The day this account was created." : ""}
            />
          </FactRow>
        </div>
      </div>

      {/* ---------------- Sign-in ---------------- */}
      <div className="crGroup">
        <CrIcon name="lock" />
        Sign-in
      </div>

      <div className="panelSub">
        <h3 className="crTitle">
          <CrIcon name="lock" size={18} />
          Password
        </h3>
        <p className="muted">
          At least {MIN_PASSWORD_LENGTH} characters. Changing it signs out every
          other browser and phone; this one stays signed in.
        </p>

        <form onSubmit={changePassword}>
          <div className="crFieldRow" style={{ marginTop: 12 }}>
            <label className="crField">
              <span className="crFieldLabel">Current password</span>
              <input
                className="inputFancy"
                type="password"
                autoComplete="current-password"
                value={pwCurrent}
                disabled={pwBusy}
                onChange={(e) => {
                  setPwCurrent(e.target.value);
                  if (pwError) setPwError("");
                }}
              />
            </label>
            <label className="crField">
              <span className="crFieldLabel">New password</span>
              <input
                className="inputFancy"
                type="password"
                autoComplete="new-password"
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                value={pwNext}
                disabled={pwBusy}
                onChange={(e) => {
                  setPwNext(e.target.value);
                  if (pwError) setPwError("");
                }}
              />
            </label>
            <label className="crField">
              <span className="crFieldLabel">New password again</span>
              <input
                className="inputFancy"
                type="password"
                autoComplete="new-password"
                value={pwConfirm}
                disabled={pwBusy}
                onChange={(e) => {
                  setPwConfirm(e.target.value);
                  if (pwError) setPwError("");
                }}
              />
            </label>
          </div>

          <div className="ctaRow">
            <button type="submit" className="btn primary" disabled={pwBusy}>
              {pwBusy ? "Changing..." : "Change password"}
            </button>
          </div>
        </form>

        {pwDone ? (
          <div className="infoBanner" style={{ marginTop: 12 }} role="status">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Password changed
            </div>
            <div className="muted small">
              Every other browser and phone signed in as you has been signed
              out. This one is still signed in.
            </div>
          </div>
        ) : null}

        {pwError ? (
          <ResultLine tone="bad" badge="Not changed">
            {pwError}
          </ResultLine>
        ) : null}

        {pwNeedsLink ? (
          <div className="ctaRow">
            <button
              type="button"
              className="btn ghost"
              onClick={emailPasswordLink}
              disabled={linkBusy || !email}
            >
              {linkBusy
                ? "Sending..."
                : pwNeedsLink === "set"
                  ? "Email me a link to set a password"
                  : "Email me a link to reset my password"}
            </button>
          </div>
        ) : null}

        {linkSent ? (
          <ResultLine tone="ok" badge="Sent">
            Check <strong>{email}</strong> for a link to set a new password. Use
            it soon: it stops working after a while.
          </ResultLine>
        ) : null}
      </div>

      {/* ---------------- Two-factor ---------------- */}
      <div className="panelSub">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 className="crTitle" style={{ margin: 0 }}>
              <CrIcon name="shield" size={18} />
              Two-factor sign-in
            </h3>
            <p className="muted" style={{ margin: "4px 0 0" }}>
              {mfaStateCopy}
            </p>
          </div>
          <span className={`chip ${mfaStateTone}`}>{mfaStateLabel}</span>
        </div>

        {/* Where setting it up actually happens. BossDrop enrols a second
            factor during sign-in, not from here: the setup endpoints only
            answer while a sign-in is in flight. So turning it off and signing
            in again is both how you switch method and how you turn it back on,
            and this card says so instead of offering a button that cannot
            work. */}
        {!mfaTurnedOff ? (
          <p className="muted small" style={{ marginTop: 10 }}>
            {mfaOn
              ? "To switch to the other method, or to a new phone, turn two-factor off here and sign in again. BossDrop walks you through setting it up, and you choose the method then."
              : "To switch it on, sign out and sign in again. BossDrop walks you through it as part of signing in."}
          </p>
        ) : null}

        {mfaTurnedOff ? (
          <div className="infoBanner" style={{ marginTop: 12 }} role="status">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Two-factor is off
            </div>
            <div className="muted small">
              BossDrop asks you to set it up again the next time you sign in.
              That is also where you choose an authenticator app or text
              messages.
            </div>
          </div>
        ) : null}

        {offNote ? (
          <ResultLine tone="warn" badge="Already off">
            {offNote}
          </ResultLine>
        ) : null}

        {/* Exactly one of these is on screen, the way the Devices card below
            does it: the trigger becomes the panel, rather than sitting above it
            with a second red button 200px underneath. */}
        {!mfaOn ? null : offOpen ? (
          <div className="panelSub" style={{ marginTop: 12 }}>
            <div className="crSubTitle">Turn off two-factor</div>
            <p className="muted small" style={{ marginTop: 0 }}>
              With it off, anyone who learns your password can sign in as you and
              read everything this workspace has connected. To be sure it is you,
              this needs your password and one more code.
            </p>
            <form onSubmit={disableMfa}>
              <div className="crFieldRow">
                <label className="crField">
                  <span className="crFieldLabel">Your password</span>
                  <input
                    className="inputFancy"
                    type="password"
                    autoComplete="current-password"
                    value={offPassword}
                    disabled={offBusy}
                    onChange={(e) => {
                      setOffPassword(e.target.value);
                      if (offError) setOffError("");
                    }}
                  />
                </label>
                <label className="crField">
                  <span className="crFieldLabel">
                    {mfaMethod === "sms"
                      ? "Code from the text"
                      : "Code from your authenticator app"}
                  </span>
                  <input
                    className="inputFancy"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={offCode}
                    disabled={offBusy}
                    onChange={(e) => {
                      setOffCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      if (offError) setOffError("");
                    }}
                  />
                  <span className="muted small">
                    {mfaMethod === "sms"
                      ? "Ask for one first: signing in used up the last code BossDrop sent you."
                      : "The 6 digits showing in your app right now."}
                  </span>
                </label>
                {/* Text-message accounts have no code to hand at this point:
                    the sign-in that let them in cleared the stored one, so
                    without this button the form could never be completed. It
                    sends on a press, never on open, because it sends a real
                    text. */}
                {mfaMethod === "sms" ? (
                  <div className="crField">
                    <span className="crFieldLabel">Need a code?</span>
                    {/* .crField is a grid, so a button in one stretches to the
                        width of the inputs beside it and starts reading as
                        another box to type in. Sized to its own text instead. */}
                    <button
                      type="button"
                      className="btn ghost"
                      style={{ justifySelf: "start" }}
                      onClick={sendDisableCode}
                      disabled={offBusy || codeBusy}
                    >
                      {codeBusy ? "Sending..." : "Text me a code"}
                    </button>
                    <span className="muted small">
                      Goes to {maskedPhone || "the number on this account"}. One
                      text every 30 seconds.
                    </span>
                  </div>
                ) : null}
              </div>

              {codeSent ? (
                <ResultLine tone="ok" badge="Sent">
                  {codeSent}
                </ResultLine>
              ) : null}
              {codeError ? (
                <ResultLine tone="bad" badge="Not sent">
                  {codeError}
                </ResultLine>
              ) : null}

              <div className="ctaRow">
                <button
                  type="submit"
                  className="btn btnDangerGhost"
                  disabled={offBusy}
                >
                  {offBusy ? "Turning it off..." : "Turn it off"}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={closeOffPanel}
                  disabled={offBusy}
                >
                  Keep it on
                </button>
              </div>
            </form>
            {offError ? (
              <ResultLine tone="bad" badge="Still on">
                {offError}
              </ResultLine>
            ) : null}
          </div>
        ) : (
          <div className="ctaRow">
            <button
              type="button"
              className="btn btnDangerGhost"
              onClick={() => {
                setOffOpen(true);
                setOffError("");
                setOffNote("");
                setCodeSent("");
                setCodeError("");
                setMfaTurnedOff(false);
              }}
            >
              Turn off two-factor
            </button>
          </div>
        )}
      </div>

      {/* ---------------- Sessions ---------------- */}
      <div className="crGroup">
        <CrIcon name="devices" />
        Devices
      </div>

      <div className="panelSub">
        <h3 className="crTitle">
          <CrIcon name="devices" size={18} />
          Signed-in devices
        </h3>
        <p className="muted">
          Signed in on a laptop you have lent out, or a phone you no longer have?
          This signs out every other browser and phone. This one stays signed in.
        </p>

        {revokeOpen ? (
          <div className="panelSub" style={{ marginTop: 12 }}>
            <div className="crSubTitle">Sign out other devices</div>
            <p className="muted small" style={{ marginTop: 0 }}>
              Everywhere else signed in as you has to sign in again, with a
              second factor if you use one. Nothing is deleted and this browser
              is not affected.
            </p>
            <div className="ctaRow">
              <button
                type="button"
                className="btn btnDangerGhost"
                onClick={revokeOthers}
                disabled={revokeBusy}
              >
                {revokeBusy ? "Signing them out..." : "Yes, sign the others out"}
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => setRevokeOpen(false)}
                disabled={revokeBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="ctaRow">
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setRevokeOpen(true);
                setRevokeDone(false);
                setRevokeError("");
              }}
            >
              Sign out other devices
            </button>
          </div>
        )}

        {revokeDone ? (
          <ResultLine tone="ok" badge="Signed out">
            Every other browser and phone has been signed out. This one is still
            signed in.
          </ResultLine>
        ) : null}
        {revokeError ? (
          <ResultLine tone="bad" badge="Not done">
            {revokeError}
          </ResultLine>
        ) : null}
      </div>
    </section>
  );
}
